import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    MedicalHistoryConfig,
    MedicalHistorySection,
    MedicalHistorySectionSettingsItem,
} from '../../../../../api/pushGenie';
import {
    DEFAULT_MEDICAL_HISTORY_CONFIG_VALUES,
    MEDICAL_HISTORY_SECTIONS_OPTION,
} from '../../../../../constants';
import { upsertMedicalHistoryConfig } from '../../../../../store/actions/session';
import { MEDICAL_HISTORY_CATEGORY, MHItem } from '../../../../../store/types/medicalHistory';
import { SlideFromRightWrapper } from '../../../../common/SlideFromRightWrapper';
import CustomiseSection, {
    SELECTED_PATIENT_HISTORY_ITEMS,
} from '../../../../medicalHistoryConfigPanel/CustomiseSections';
import SelectionSection from '../../../../medicalHistoryConfigPanel/SelectionSection';
import { MISC_KEYS, getItemFromMisc } from '../../../../../store/idbHandler';
import { GlobalState } from '../../../../../store/reducers';
import { getMyIdWithBackwardCompatibility } from '../../../../../api/mactiveid';

const PPMedicalHistoryConfigPanel: React.FC<{
    closeMe: () => void;
}> = ({ closeMe }) => {
    const dispatch = useDispatch();
    const { commonItems, sections } = DEFAULT_MEDICAL_HISTORY_CONFIG_VALUES;
    const [medicalHistoryConfigData, setMedicalHistoryConfigData] = useState<MedicalHistoryConfig>(
        DEFAULT_MEDICAL_HISTORY_CONFIG_VALUES,
    );

    const [selectedTab, setSelectedTab] = useState('customise-section');

    const [medicalHistorySectionConfig, setMedicalHistorySectionConfig] = useState<
        MedicalHistorySectionSettingsItem[]
    >(sections.map((i) => ({ ...i, isShown: true })));

    const [medicalHistoryCommonItems, setMedicalHistoryCommonItems] = useState<MHItem[]>(
        commonItems,
    );
    const rxDocId = useSelector(
        (state: GlobalState) => state.prescriptionPad.prescription?.doctor?._id,
    );

    const setMedicalHistoryCommonItemsSectionData = (
        catId: MEDICAL_HISTORY_CATEGORY,
        option: MHItem,
    ) => {
        const items = {
            catId: catId,
            id: option.id,
            name: option.name,
        };

        setMedicalHistoryCommonItems([...medicalHistoryCommonItems, items]);
        const containerId = document.getElementById(
            SELECTED_PATIENT_HISTORY_ITEMS,
        ) as HTMLDivElement;
        containerId.scrollTo({
            top: containerId?.scrollHeight + 10000,
            behavior: 'smooth',
        });
    };

    const getMedicalHistoryConfigData = () => {
        const myId = getMyIdWithBackwardCompatibility() || '';
        const currentUserId = rxDocId || myId || '';
        getItemFromMisc<MedicalHistoryConfig>(currentUserId, MISC_KEYS.medicalHistoryConfig)
            .then((medicalHistoryConfigData) => {
                setMedicalHistoryConfigData(medicalHistoryConfigData);
            })
            .catch(console.error);
    };

    useEffect(getMedicalHistoryConfigData, []);

    useEffect(() => {
        const sectionItemsKeysSet = new Set(medicalHistoryConfigData?.sections?.map((r) => r.key));
        const mergedSectionAndDefaultConfig = [
            ...new Map(
                [
                    ...(medicalHistoryConfigData?.sections || []),
                    ...MEDICAL_HISTORY_SECTIONS_OPTION,
                ].map((item) => [item['key'], item]),
            ).values(),
        ];

        setMedicalHistorySectionConfig(
            mergedSectionAndDefaultConfig.map((i) => {
                return {
                    ...i,
                    isShown: sectionItemsKeysSet.has(i.key),
                };
            }),
        );

        setMedicalHistoryCommonItems([...(medicalHistoryConfigData?.commonItems || [])]);
    }, [medicalHistoryConfigData]);

    const setSectionSection = (): void => {
        setSelectedTab('select-section');
    };

    const setCustomiseSection = (): void => {
        setSelectedTab('customise-section');
    };

    function deleteMedicalHistoryCommonItemsElement(id: string) {
        const data = medicalHistoryCommonItems.filter((e) => e.id !== id);
        setMedicalHistoryCommonItems(data);
    }

    const onSave = async (): Promise<void> => {
        const medicalHistorySectionConfigData: MedicalHistorySection[] = medicalHistorySectionConfig
            .filter((d) => d.isShown)
            .map((d) => ({
                label: d.label,
                type: 'string',
                key: d.key,
            }));

        const medicalHistoryConfigData = {
            commonItems: medicalHistoryCommonItems,
            sections: medicalHistorySectionConfigData,
        };

        const myId = getMyIdWithBackwardCompatibility() || '';
        dispatch(upsertMedicalHistoryConfig(medicalHistoryConfigData, rxDocId || myId));

        setMedicalHistoryConfigData(medicalHistoryConfigData);

        closeMe();
    };

    return (
        <SlideFromRightWrapper closeMe={closeMe} cssClass="w-70p">
            <div
                className="bg-darwin-neutral-50 flex flex-col h-full space-between w-full"
                id="CUSTOMISE_MEDICAL_HISTORY"
            >
                <div className="flex items-center space-x-16 p-16 pb-24 cursor-pointer">
                    <div onClick={closeMe} className="flex items-center justify-center w-16 h-16">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 448 512"
                            className="w-16 h-16 fill-darwin-neutral-1000"
                        >
                            <path d="M447.1 256c0 13.25-10.76 24.01-24.01 24.01H83.9l132.7 126.6c9.625 9.156 9.969 24.41 .8125 33.94c-9.156 9.594-24.34 9.938-33.94 .8125l-176-168C2.695 268.9 .0078 262.6 .0078 256S2.695 243.2 7.445 238.6l176-168C193 61.51 208.2 61.85 217.4 71.45c9.156 9.5 8.812 24.75-.8125 33.94l-132.7 126.6h340.1C437.2 232 447.1 242.8 447.1 256z"></path>
                        </svg>
                    </div>
                    <p className="text-darwin-neutral-1000 heading-2 bold">
                        Customise medical history
                    </p>
                </div>

                <div className="w-full border-b border-neutral-8 select-none">
                    <div className="flex items-center font-400 text-14 leading-28 cursor-pointer ml-8">
                        <div
                            className={
                                selectedTab === 'customise-section'
                                    ? 'border-b-2 mx-24 border-actionPrimary  text-14'
                                    : 'border-b-2 mx-24 border-transparent text-14 '
                            }
                            onClick={setCustomiseSection}
                        >
                            <p
                                className={
                                    selectedTab === 'customise-section'
                                        ? 'pb-8 text-actionPrimary text-14 font-700'
                                        : 'pb-8 text-neutral-3 text-14'
                                }
                            >
                                Quick Links
                            </p>
                        </div>
                        <div
                            className={
                                selectedTab === 'select-section'
                                    ? 'border-b-2 border-actionPrimary  text-14'
                                    : 'border-b-2 border-transparent text-14'
                            }
                            onClick={setSectionSection}
                        >
                            <p
                                className={
                                    selectedTab === 'select-section'
                                        ? 'pb-8 text-actionPrimary text-14 font-700'
                                        : 'pb-8 text-neutral-3 text-14'
                                }
                            >
                                Select sections
                            </p>
                        </div>
                    </div>
                </div>

                <div className="p-16 flex flex-col space-y-16 pb-0 overflow-auto">
                    <div className="flex items-center p-12 rounded-8 bg-white bg-darwin-accent-investigations-ochre-100 border border-darwin-accent-investigations-ochre-500">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="11"
                            height="15"
                            viewBox="0 0 11 15"
                            fill="none"
                        >
                            <path
                                d="M3.3125 13.1914C3.3125 13.3555 3.33984 13.5195 3.44922 13.6562L3.91406 14.3672C4.05078 14.5859 4.37891 14.75 4.65234 14.75H6.32031C6.59375 14.75 6.92188 14.5859 7.05859 14.3672L7.52344 13.6562C7.60547 13.5469 7.66016 13.3281 7.66016 13.1914L7.6875 12.125H3.3125V13.1914ZM5.47266 0.777344C2.68359 0.777344 0.6875 3.01953 0.6875 5.5625C0.6875 6.76562 1.125 7.85938 1.86328 8.70703C2.32812 9.22656 3.01172 10.3203 3.28516 11.2227C3.28516 11.2227 3.28516 11.2227 3.28516 11.25H7.6875C7.6875 11.2227 7.6875 11.2227 7.6875 11.2227C7.96094 10.3203 8.64453 9.22656 9.10938 8.70703C9.84766 7.88672 10.3125 6.79297 10.3125 5.5625C10.3125 2.91016 8.125 0.75 5.47266 0.777344ZM5.5 3.40234C4.26953 3.40234 3.3125 4.35938 3.3125 5.5625C3.3125 5.80859 3.09375 6 2.875 6C2.62891 6 2.4375 5.80859 2.4375 5.5625C2.4375 3.89453 3.80469 2.52734 5.5 2.52734C5.71875 2.52734 5.9375 2.74609 5.9375 2.96484C5.9375 3.21094 5.71875 3.40234 5.5 3.40234Z"
                                fill="#796736"
                            />
                        </svg>
                        <p className="body-1 ml-8 text-darwin-neutral-1000">
                            {selectedTab === 'select-section'
                                ? "The selected sections will be visible under 'medical history'"
                                : 'The options you select here will be visible on pad for quick addition.'}
                        </p>
                    </div>
                </div>
                {selectedTab === 'select-section' ? (
                    <SelectionSection
                        medicalHistorySectionConfig={medicalHistorySectionConfig}
                        setMedicalHistorySectionConfig={setMedicalHistorySectionConfig}
                    />
                ) : (
                    <CustomiseSection
                        setMedicalHistoryCommonItemsSectionData={
                            setMedicalHistoryCommonItemsSectionData
                        }
                        medicalHistoryCommonItems={medicalHistoryCommonItems}
                        deleteMedicalHistoryCommonItemsElement={
                            deleteMedicalHistoryCommonItemsElement
                        }
                    />
                )}
                <div className="flex items-center justify-between absolute rounded-8 bg-actionPrimary bottom-24 inset-x-10 px-16 py-8 cta-row">
                    <button onClick={closeMe} className="text-btn-v2 px-16 py-8 btn--small">
                        Cancel
                    </button>
                    <button
                        className="px-16 py-8 btn--small primary-btn-v2 tooltip-wrapper"
                        onClick={onSave}
                    >
                        <div className="flex items-center justify-center w-16 h-16">
                            <svg
                                className="w-16 h-16 fill-actionPrimary"
                                role="img"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 448 512"
                            >
                                <path d="M264.6 70.63l176 168c4.75 4.531 7.438 10.81 7.438 17.38s-2.688 12.84-7.438 17.38l-176 168c-9.594 9.125-24.78 8.781-33.94-.8125c-9.156-9.5-8.812-24.75 .8125-33.94l132.7-126.6H24.01c-13.25 0-24.01-10.76-24.01-24.01s10.76-23.99 24.01-23.99h340.1l-132.7-126.6C221.8 96.23 221.5 80.98 230.6 71.45C239.8 61.85 254.1 61.51 264.6 70.63z"></path>
                            </svg>
                        </div>
                        <p>Save Changes</p>
                    </button>
                </div>
            </div>
        </SlideFromRightWrapper>
    );
};

export default PPMedicalHistoryConfigPanel;
